import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { NavLink, useLocation } from "react-router-dom";
import { LayoutList, Calendar, Users, Gift, FileText, BarChart3, LogOut, Inbox } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import { useToast } from "@/hooks/use-toast";
import { useUnreadMessagesCount } from "@/hooks/useContactMessages";
interface AdminNavbarProps {
  isOpen: boolean;
  toggle: () => void;
}
const SUPABASE_LOGO_URL = "/images/logo-bbz-sans-baseline.png";
const AdminNavbar = ({
  isOpen,
  toggle
}: AdminNavbarProps) => {
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { logout } = useAdminAuth();
  const { toast } = useToast();
  const { data: unreadCount = 0 } = useUnreadMessagesCount();

  const handleLogout = () => {
    logout();
    toast({
      title: "Déconnexion",
      description: "Vous avez été déconnecté avec succès"
    });
  };

  // Determine redirect URL based on current admin section
  const getLogoRedirectUrl = () => {
    const currentPath = location.pathname;
    switch (currentPath) {
      case "/admin/services":
        return "/soins-visage-massages-epilations-montpellier";
      // Carte des soins
      case "/admin/calendar":
        return "/";
      // Page d'accueil
      case "/admin/hours":
        return "/";
      // Page d'accueil (non spécifié, donc accueil par défaut)
      case "/admin/clients":
        return "/";
      // Page d'accueil
      case "/admin/gift-cards":
        return "/bon-cadeaux-estheticienne-bio-montpellier";
      // Bon cadeau
      case "/admin/info":
        return "/soins-visage-massages-epilations-montpellier";
      // Carte des soins
      default:
        return "/";
      // Page d'accueil par défaut
    }
  };
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isOpen) {
      document.documentElement.style.overflow = 'hidden';
      document.documentElement.style.overscrollBehavior = 'contain';
    } else {
      document.documentElement.style.overflow = '';
      document.documentElement.style.overscrollBehavior = '';
    }
    return () => {
      document.documentElement.style.overflow = '';
      document.documentElement.style.overscrollBehavior = '';
    };
  }, [isOpen]);
  const navigationItems = [{
    to: "/admin/messages",
    icon: Inbox,
    label: "Messages",
    title: "Messagerie - répondre aux messages du formulaire de contact",
    badge: unreadCount
  }, {
    to: "/admin/services",
    icon: LayoutList,
    label: "Catégories & Services",
    title: "Gérer les catégories et services"
  }, {
    to: "/admin/calendar",
    icon: Calendar,
    label: "Agenda",
    title: "Consulter et gérer l'agenda"
  }, {
    to: "/admin/clients",
    icon: Users,
    label: "Clients",
    title: "Gérer la base clients"
  }, {
    to: "/admin/gift-cards",
    icon: Gift,
    label: "Bons Cadeaux",
    title: "Créer et gérer les bons cadeaux"
  }, {
    to: "/admin/statistics",
    icon: BarChart3,
    label: "Statistiques",
    title: "Consulter les statistiques de l'institut"
  }, {
    to: "/admin/info",
    icon: FileText,
    label: "Informations",
    title: "Modifier les informations générales"
  }];
  return <>
      <header className={`fixed w-full transition-all duration-300 ${scrolled ? "bg-white/95 backdrop-blur-sm shadow-md py-2" : "bg-transparent py-4"} z-30`}>
        <div className="container mx-auto px-4 flex justify-between items-center">
          <div className="flex items-center">
            <NavLink to={getLogoRedirectUrl()} aria-label="Retour au site principal" title="Retour vers la section correspondante du site">
              <img src={SUPABASE_LOGO_URL} alt="Logo Bio Beauté Zen - Administration" loading="eager" className="max-h-7 w-auto" />
            </NavLink>
            <NavLink to="/admin" className="ml-3 font-poppins font-semibold text-bbz-text hidden sm:block hover:text-bbz-green transition-colors" aria-label="Accueil administration" title="Aller vers la page d'administration">
              Administration
            </NavLink>
          </div>

          {/* Mobile/Tablet: Show "Administration" text */}
          

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center" aria-label="Navigation administration">
            <div className="flex space-x-1 xl:space-x-4">
              {navigationItems.map(item => {
              const Icon = item.icon;
              const badge = (item as any).badge as number | undefined;
              return <NavLink key={item.to} to={item.to} title={item.title} className={({
                isActive
              }) => isActive ? "relative font-medium text-bbz-text border-b-2 border-bbz-green px-2 py-2 whitespace-nowrap flex items-center transition-colors" : "relative font-medium text-bbz-text hover:text-bbz-green transition-colors px-2 py-2 whitespace-nowrap flex items-center"}>
                    <span className="relative">
                      <Icon className="h-4 w-4 mr-1 xl:mr-2" />
                      {badge ? (
                        <span className="absolute -top-2 -right-1 bg-destructive text-destructive-foreground text-[9px] min-w-[16px] h-[16px] px-1 rounded-full flex items-center justify-center font-semibold">
                          {badge > 9 ? "9+" : badge}
                        </span>
                      ) : null}
                    </span>
                    <span className="hidden xl:block">{item.label}</span>
                    <span className="xl:hidden text-sm">{item.label.split(' ')[0]}</span>
                  </NavLink>;
            })}
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="ml-4 text-bbz-text hover:text-bbz-green"
              title="Se déconnecter"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </nav>
        </div>
      </header>

    </>;
};
export default AdminNavbar;